'use client';

import type { SdkImportWalletParams as ImportWalletParams } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { InputField } from '@/components/ui/input-field';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import type { Wallet } from '@/types/auth';

interface ImportWalletProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportWallet: (params: ImportWalletParams) => Promise<Wallet | null>;
  /** Called before Turnkey's modal opens so parent modals can dismiss. */
  onBeforeTurnkeyModal?: () => void;
}

/**
 * Import wallet dialog — collects a wallet label, then delegates to
 * Turnkey's managed import modal for secure private key entry.
 *
 * Only private key import is supported (one key = one wallet).
 * Seed phrase import was removed to avoid ambiguity with multi-account
 * derivation paths.
 */
export function ImportWallet({
  open,
  onOpenChange,
  onImportWallet,
  onBeforeTurnkeyModal,
}: ImportWalletProps): React.ReactNode {
  const t = useTranslations('wallet.import');
  const { announce } = useAnnouncer();
  const { enabled: multiWalletsEnabled, canStartOperation } = useMultiWalletFeature();
  const [label, setLabel] = useState<string>('');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  function handleOpenChange(next: boolean): void {
    onOpenChange(next);
    if (!next) {
      setIsImporting(false);
      setError(null);
      setLabel('');
    }
  }

  async function handleImport(): Promise<void> {
    if (!canStartOperation('import', 'wallet_management')) {
      onOpenChange(false);
      return;
    }
    setIsImporting(true);
    setError(null);

    await runWithCleanup(
      async () => {
        try {
          // Build params — always private key import, Turnkey's modal handles key entry
          const trimmedLabel = label.trim();
          const params: ImportWalletParams = {
            importType: 'privateKey',
            ...(trimmedLabel.length > 0 ? { label: trimmedLabel } : {}),
          };

          // Close our dialog AND parent modals BEFORE Turnkey's import modal opens.
          // Without this, modals stack on top of each other.
          onOpenChange(false);
          onBeforeTurnkeyModal?.();

          const importedWallet = await onImportWallet(params);
          if (importedWallet === null) return;

          announce('Wallet imported successfully');
          showToast({
            id: TOAST_IDS.WALLET_IMPORTED,
            message: t('success'),
            variant: 'success',
          });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          logger.warn('Wallet import caught error', { error: msg });
          const causeMsg =
            err instanceof Error && err.cause instanceof Error ? err.cause.message : '';
          const fullMsg = `${msg} ${causeMsg}`.toLowerCase();

          if (
            fullMsg.includes('cancel') ||
            fullMsg.includes('closed') ||
            fullMsg.includes('dismissed')
          ) {
            logger.info('Wallet import cancelled by user');
          } else if (
            fullMsg.includes('already been imported') ||
            fullMsg.includes('already imported')
          ) {
            showToast({
              id: TOAST_IDS.WALLET_ERROR,
              message: t('already_imported'),
              variant: 'error',
            });
          } else {
            logger.error('Wallet import failed', { error: msg.slice(0, 200) });
            setError(msg);
            onOpenChange(true);
          }
        }
      },
      () => setIsImporting(false),
    );
  }

  return (
    <SilkSheet
      open={open && multiWalletsEnabled}
      onOpenChange={handleOpenChange}
      variant="detached"
      size="md"
      keyboard
      title={t('title')}
      description={t('hint_key')}
      sheetClassName="sm:aspect-[4/3]"
      footer={
        <ModalFooter>
          <AnimatedButton
            variant="secondary"
            onClick={(): void => handleOpenChange(false)}
            disabled={isImporting}
          >
            {t('cancel')}
          </AnimatedButton>
          <AnimatedButton
            type="button"
            onClick={handleImport}
            disabled={isImporting}
            loading={isImporting}
          >
            {isImporting ? t('importing') : t('submit')}
          </AnimatedButton>
        </ModalFooter>
      }
    >
      <InputField
        id="import-wallet-name"
        label={t('label_input')}
        type="text"
        value={label}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          setLabel(e.target.value);
        }}
        placeholder={t('label_placeholder')}
        disabled={isImporting}
        autoComplete="off"
        data-dd-privacy="mask"
        error={error ?? undefined}
      />
    </SilkSheet>
  );
}
